'use strict';

/**
 * Title Case a free-text value, e.g. "JOHN tan" -> "John Tan".
 * Mirrors the server-side titleCase() in students.controller.js.
 * Returns '' for null/undefined.
 */
export function titleCase(value) {
  if (!value) return '';
  return String(value).trim().toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Format any date string or Date object to DD MMM YYYY
 * e.g. "2026-01-15" → "15 Jan 2026"
 * Returns "—" for null/undefined/invalid
 */
export function fmtDate(value) {
  if (!value) return '—';
  // A plain 'YYYY-MM-DD' string (what DATE columns come back as) is parsed
  // by `new Date()` as UTC midnight per spec, but toLocaleDateString then
  // renders it in the viewer's local timezone — for anyone west of UTC that
  // silently rolls the date back a day. Build the date from its own
  // calendar components instead, so it never touches UTC conversion at all.
  const dateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = dateOnly
    ? new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10)))
    : new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day:   '2-digit',
    month: 'short',
    year:  'numeric',
  }); // e.g. "15 Jan 2026"
}

/**
 * Session/schedule_name in the external QR system is set to the module_code,
 * not a human-readable name — resolve it to "Module Name (module_code)" when
 * a matching module is known, falling back to the raw value otherwise (e.g.
 * legacy sessions predating module_code, or an unmatched code).
 */
export function fmtSessionName(rec) {
  if (!rec) return '';
  const name = rec.session_module_name;
  const code = rec.session_name;
  if (name && code) return `${name} (${code})`;
  return code || name || '';
}

/**
 * Format a claimed/worked hours value: by default a whole number reads as a
 * whole number (1, not 1.00) and fractional values get one decimal place
 * (1.5, not 1.50). Pass `decimals` when a screen needs fixed precision, and
 * `unit: true` to append a singular/plural "hr"/"hrs".
 */
export function fmtHours(value, { unit = false, decimals } = {}) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (isNaN(n)) return '—';
  const str = Number.isInteger(decimals) && decimals >= 0
    ? n.toFixed(decimals)
    : Number.isInteger(n) ? String(n) : n.toFixed(1);
  return unit ? `${str} ${n === 1 ? 'hr' : 'hrs'}` : str;
}

/**
 * Format datetime to DD MMM YYYY, HH:MM
 * e.g. "2026-01-15T08:02:00" → "15 Jan 2026, 08:02"
 */
export function fmtDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('en-GB', {
    day:   '2-digit',
    month: 'short',
    year:  'numeric',
  });
  const time = d.toLocaleTimeString('en-GB', {
    hour:   '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${date}, ${time}`;
}

/**
 * Format a percentage for the screen: up to two decimal places, with the
 * unnecessary ones dropped (86 -> "86%", 85.5 -> "85.5%", 86.36 -> "86.36%").
 * Pass { fixed: true } for exports/print where a fixed width is wanted
 * ("85.50%"). Returns "—" for null/undefined/blank/NaN.
 */
export function fmtPct(value, { fixed = false } = {}) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${fixed ? n.toFixed(2) : String(Number(n.toFixed(2)))}%`;
}
